import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useEditorLock (T-026, Req. B) — lock the editor while an AI /ai/chat request
 * is in flight, with a hard fail-safe release.
 *
 * Owner requirement: block editing while the AI is working; on failure, hang, or
 * cancel, control returns to the user. Architect calibration: lock ONLY while the
 * request is in flight (NOT while a proposal card waits for "Aplicar" — Apply
 * recounts against the live document).
 *
 * The lock is released in a `finally` (success, HTTP error, OR abort), by a hard
 * client timeout that aborts a hung request, and on unmount (effect cleanup), so
 * a stuck request can never leave the editor permanently read-only.
 *
 * @param {() => (import('@tiptap/react').Editor | null | undefined)} getEditor
 *        Returns the live TipTap editor instance (or null if not ready).
 * @returns {{ locked: boolean, runLocked: Function, cancel: () => void }}
 *   - `locked`: true while a request is in flight (drives the UI indicator).
 *   - `runLocked(requestFn, timeoutMs?)`: locks, then awaits `requestFn(signal)`;
 *     pass the AbortSignal to axios so `cancel()`/timeout actually abort the
 *     request. `timeoutMs` (optional) overrides the fail-safe hard timeout for
 *     THIS acquisition only — T-034 macros pass N*120000 so the lock covers all
 *     N sequential steps without the fail-safe releasing it mid-macro.
 *   - `cancel()`: abort the in-flight request (releases via the `finally`).
 */
const HARD_TIMEOUT_MS = 120000; // 120s default: a hung request can never hold the lock.

export default function useEditorLock(getEditor) {
  const [locked, setLocked] = useState(false);

  // Keep a live ref to getEditor so the callbacks/effect stay stable across
  // renders (a new inline getEditor each render must NOT re-run the cleanup).
  const getEditorRef = useRef(getEditor);
  getEditorRef.current = getEditor;

  const controllerRef = useRef(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const setEditable = useCallback((value) => {
    try {
      const editor = getEditorRef.current && getEditorRef.current();
      if (editor && !editor.isDestroyed) editor.setEditable(value);
    } catch (_) {
      // Editor already gone: nothing to (un)lock.
    }
  }, []);

  const release = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    controllerRef.current = null;
    setEditable(true);
    if (mountedRef.current) setLocked(false);
  }, [setEditable]);

  const cancel = useCallback(() => {
    if (controllerRef.current) controllerRef.current.abort();
  }, []);

  const runLocked = useCallback(
    async (requestFn, timeoutMs) => {
      // Defensive: abort any prior request still tracked before starting.
      if (controllerRef.current) controllerRef.current.abort();

      const controller = new AbortController();
      controllerRef.current = controller;
      setEditable(false);
      setLocked(true);
      // Per-acquisition fail-safe: default 120s; callers running several
      // sequential requests under ONE lock (T-034 macros) pass a larger budget.
      const hardTimeout =
        Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : HARD_TIMEOUT_MS;
      timerRef.current = setTimeout(() => controller.abort(), hardTimeout);

      try {
        return await requestFn(controller.signal);
      } finally {
        // Fail-safe: release on success, HTTP error, AND abort.
        release();
      }
    },
    [release, setEditable]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (controllerRef.current) controllerRef.current.abort();
      // Fail-safe: never leave the editor locked if we unmount mid-flight.
      setEditable(true);
    };
  }, [setEditable]);

  return { locked, runLocked, cancel };
}
